import React from 'react';

/**
 * The tutorial's control areas while taps play: Next and Keep practicing (the
 * step buttons) and the video's bottom-right controls, the volume panel and
 * Pause included.
 */
export const TUTORIAL_CONTROL_AREAS = '#tutorial-step-buttons, [data-tutorial-controls]';

const inControlArea = (target: EventTarget | null): boolean =>
  target instanceof Element && target.closest(TUTORIAL_CONTROL_AREAS) !== null;

/**
 * A panel a control opens in place (the volume panel). Keyboard focus may
 * stay in it while it is open.
 */
export const CONTROL_PANEL = '[data-control-panel]';

/** Hand focus back to the page if a control area still holds it. */
const returnFocusToPage = () => {
  const focused = document.activeElement;
  if (focused instanceof HTMLElement && inControlArea(focused)) focused.blur();
};

/**
 * After a keyboard press: keep focus on a control that now has its popup open
 * (`aria-expanded="true"`) or that sits inside an open panel, so the focus ring
 * and a screen reader's place are kept; otherwise hand it back.
 */
const returnKeyboardFocusToPage = () => {
  const focused = document.activeElement;
  if (!(focused instanceof HTMLElement)) return;
  if (focused.getAttribute('aria-expanded') === 'true') return;
  if (focused.closest(CONTROL_PANEL)) return;
  returnFocusToPage();
};

/** A click made by Space / Enter on a button reads `detail` 0; a pointer's is 1 or more. */
const isKeyboardClick = (event: Event): boolean =>
  event.type === 'click' && (event as MouseEvent).detail === 0;

/**
 * While taps play, Space and Enter are taps, except on a control the player
 * has moved to with Tab. So once a control in the tutorial's control areas
 * has been USED, focus goes back to the page and the next Space is a tap:
 *   - pressed by any input (a click, which Space / Enter on a button also
 *     makes, or the release of Space / Enter on a control that acts on
 *     keydown);
 *   - a pointer press or drag ended on it (a slider's thumb);
 *   - a panel closing takes its focused element with it, and Escape that
 *     closes it hands back the control that opened it.
 * Focus moved with Tab is left where it is, so a keyboard player can still
 * reach a control and press it. A keyboard press that opens a panel, or one
 * made inside an open panel, keeps focus there until the panel closes. The hand-back runs after the event, so the
 * control's own action (a keyup click, a ripple) is not cut short.
 */
export function useReturnFocusAfterControlUse(active: boolean): void {
  React.useEffect(() => {
    if (!active) return undefined;
    // Not cancelled when taps stop: pressing Next on an objective's last
    // pass ends the listening in the same moment, and Next can come back into
    // view at the next exercise still holding focus.
    const afterThisEvent = (event: Event) => {
      if (!inControlArea(event.target)) return;
      setTimeout(isKeyboardClick(event) ? returnKeyboardFocusToPage : returnFocusToPage, 0);
    };
    // Space / Enter press a control; Escape closes its panel (and the control
    // that opened it is then done with).
    const afterPressKey = (event: KeyboardEvent) => {
      if (![' ', 'Enter', 'Escape'].includes(event.key)) return;
      if (inControlArea(event.target)) setTimeout(returnKeyboardFocusToPage, 0);
    };
    // Capture phase: noted before the app handles the event, so a press that
    // stops the listening (and removes these) is still noted.
    document.addEventListener('click', afterThisEvent, true);
    document.addEventListener('pointerup', afterThisEvent, true);
    document.addEventListener('keyup', afterPressKey, true);
    return () => {
      document.removeEventListener('click', afterThisEvent, true);
      document.removeEventListener('pointerup', afterThisEvent, true);
      document.removeEventListener('keyup', afterPressKey, true);
    };
  }, [active]);
}
